/**
 * Faixa de bandeiras e plano na barra superior (Figma; out/2026 — veio do topo
 * do Dashboard para a barra, a pedido do Bruno, quando o menu foi para a
 * lateral): bandeira tarifária oficial do mês e do mês anterior + resumo do
 * plano (nome, imóveis usados/limite e usinas ligadas a monitoramento). Tudo do
 * Redux carregado no login — nenhuma chamada nova; aparece em todas as telas.
 *
 * No fundo escuro da barra: a bandeira é só o ícone na cor dela (sem quadrado);
 * a de escassez hídrica é preta e sumiria, então vai em contorno branco; mês
 * sem bandeira publicada fica "a publicar" com contorno cinza (não repete a do
 * mês anterior). Telas menores mostram só ícone + valor (rótulo no title).
 *
 * O Figma mostra "Plantas online 29/30"; o front não tem o status das usinas
 * (ele vive no cron de monitoramento do backend), então fica a contagem.
 */
import React, { useMemo } from 'react';
import {
  BsFlag,
  BsFlagFill,
  BsBuilding,
  BsBroadcast,
  BsAward,
} from 'react-icons/bs';

import { useAppSelector } from 'store/hooks';
import { selectCurrentFlagList } from 'store/current_flag/current_flag.selector';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import * as selectUser from 'store/user/user.selector';
import { CurrentFlag } from 'entities/CurrentFlag';
import { EFlagColor } from 'utils/constants';
import { getPlanRuleInfo } from 'utils/planRules';

import {
  StatusBar,
  Group,
  GroupLabel,
  Item,
  ItemIcon,
  ItemText,
} from './navbar-status.styles';

/** Bandeira publicada para o mês (ano/mês em UTC, como `utils/flag`). */
const flagOfMonth = (list: CurrentFlag[], year: number, month: number) =>
  list.find((x) => {
    const d = new Date(x.fromDate);
    return d.getUTCFullYear() === year && d.getUTCMonth() === month;
  });

const FlagItem = ({ label, flag }: { label: string; flag?: CurrentFlag }) => {
  const slug = flag?.flag.slug as keyof typeof EFlagColor | undefined;
  const name = flag?.flag.name ?? 'a publicar';
  // escassez hídrica (preta) e "a publicar" em contorno — o preenchido some
  const outline = !slug || slug === 'escassez_hidrica';
  return (
    <Item title={`${label}: ${name}`}>
      <ItemIcon $color={outline ? undefined : EFlagColor[slug]} $muted={!slug}>
        {outline ? <BsFlag /> : <BsFlagFill />}
      </ItemIcon>
      <ItemText>
        <small>{label}</small>
        <b>{name}</b>
      </ItemText>
    </Item>
  );
};

export const NavbarStatus = () => {
  const currentFlagList = useAppSelector(selectCurrentFlagList);
  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const currentUser = useAppSelector(selectUser.currentUser);

  const [thisMonth, lastMonth] = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const prevY = m === 0 ? y - 1 : y;
    const prevM = m === 0 ? 11 : m - 1;
    return [
      flagOfMonth(currentFlagList, y, m),
      flagOfMonth(currentFlagList, prevY, prevM),
    ];
  }, [currentFlagList]);

  const planName = currentUser?.plan?.name;
  const maxUnits = planName
    ? getPlanRuleInfo(planName)?.maxConsumerUnits
    : undefined;
  const units = `${consumerUnitList.length}${
    maxUnits && Number.isFinite(maxUnits) ? ` / ${maxUnits}` : ''
  }`;
  const monitoredPlants = consumerUnitList.reduce(
    (sum, uc) => sum + (uc.plantList?.length ?? 0),
    0
  );

  return (
    <StatusBar aria-label="Bandeiras e plano">
      <Group>
        <GroupLabel>Bandeiras</GroupLabel>
        <FlagItem label="Este mês" flag={thisMonth} />
        <FlagItem label="Mês anterior" flag={lastMonth} />
      </Group>

      <Group>
        <GroupLabel>Meu plano</GroupLabel>
        {planName && (
          <Item title={`Plano: ${planName}`}>
            <ItemIcon>
              <BsAward />
            </ItemIcon>
            <ItemText>
              <small>Plano</small>
              <b>{planName}</b>
            </ItemText>
          </Item>
        )}
        <Item title={`Imóveis: ${units}`}>
          <ItemIcon>
            <BsBuilding />
          </ItemIcon>
          <ItemText>
            <small>Imóveis</small>
            <b>{units}</b>
          </ItemText>
        </Item>
        <Item title={`Usinas monitoradas: ${monitoredPlants}`}>
          <ItemIcon>
            <BsBroadcast />
          </ItemIcon>
          <ItemText>
            <small>Usinas</small>
            <b>{monitoredPlants}</b>
          </ItemText>
        </Item>
      </Group>
    </StatusBar>
  );
};
